import { useCallback, useState } from "react";

import { useMessagesInProgress } from "@doitintl/ava-components/src/Messenger/hooks";
import { Stack } from "@mui/material";

import { useCustomerContext } from "../../Context/CustomerContext";
import { useMetadata } from "../Ava/Messenger/hooks";
import LoadingCustomData from "../Ava/Messenger/LoadingCustomData";
import { useAvaContext } from "./AvaContext";
import { useAvaDialogContext } from "./AvaDialogContextProvider";
import { useNewQuestionAsked } from "./hooks";
import { MessageList } from "./MessageList";
import { MessengerFooter } from "./MessengerFooter";
import { useAva } from "./useAva";
import { upload } from "./utils";

export const Messenger = () => {
  const [processingQuestion, setProcessingQuestion] = useState(false);
  const [generationStopped, setGenerationStopped] = useState(false);

  const [error, setError] = useState<string | undefined>();
  const [question, setQuestion] = useState("");

  const { customer } = useCustomerContext({ allowNull: true });

  const { updateCustomerMetadata } = useMetadata();
  const { conversationId, setConversationId } = useAvaContext();
  const { newQuestionAsked, setNewQuestionAsked } = useNewQuestionAsked();
  const { setConversationHasOccurred } = useAvaDialogContext();

  const handleFinishGeneration = useCallback(() => {
    setProcessingQuestion(false);
    setGenerationStopped(false);
  }, []);
  const getAbortStatus = useCallback(() => generationStopped, [generationStopped]);

  const { answer, answerExtras, askQuestion, onStopGeneration, overrideLongerWait, resetAnswer, avaChatLoading } =
    useAva(handleFinishGeneration, "console", getAbortStatus);

  const { messageUserInProgress, messageAvaInProgress } = useMessagesInProgress(
    generationStopped,
    question,
    answer,
    answerExtras
  );

  const handleStopGeneration = useCallback(() => {
    setGenerationStopped(true);
    setProcessingQuestion(false);
    if (conversationId && answerExtras?.answerId) {
      onStopGeneration(conversationId, answerExtras?.answerId, answer);
    }
  }, [conversationId, answerExtras?.answerId, answer, onStopGeneration]);

  const handleAskQuestion = async (question: string, files?: File[]) => {
    setProcessingQuestion(true);
    setError(undefined);
    setGenerationStopped(false);
    setNewQuestionAsked(true);
    resetAnswer();

    let questionWithFiles = question;

    try {
      if (files && files.length > 0) {
        const fileNames = files?.map((file) => file.name).join(", ");
        questionWithFiles = `${question} \n\n $file-names: ${fileNames}`;
        setQuestion(questionWithFiles);
        const uploadedFiles = await upload(files);
        if (uploadedFiles && uploadedFiles.length > 0) {
          questionWithFiles = `${question} \n\n $attachments: ${uploadedFiles.join(",\n")}`;
        }
      }

      setQuestion(questionWithFiles);
      await askQuestion({
        conversationId,
        question: questionWithFiles,
        conversationCallback: (conversationId) => {
          setConversationId(conversationId);
          setConversationHasOccurred(true);
          updateCustomerMetadata();
        },
      });
    } catch (e: unknown) {
      setError(String(e));
    }
  };

  return (
    <Stack
      sx={{
        backgroundColor: "general.backgroundDark",
        justifyContent: "flex-end",
        overflow: "auto",
        flex: 1,
      }}
    >
      <MessageList
        conversationId={conversationId}
        messageUserInProgress={messageUserInProgress}
        messageAvaInProgress={messageAvaInProgress}
        handleAskQuestion={handleAskQuestion}
        isGeneratingReport={overrideLongerWait}
        processingQuestion={processingQuestion}
        error={error}
        newQuestionAsked={newQuestionAsked}
        avaChatLoading={avaChatLoading}
      />

      {customer?.id && <LoadingCustomData customerId={customer.id} />}
      <MessengerFooter
        onAskQuestionClicked={handleAskQuestion}
        disableInput={processingQuestion}
        showStopButton={processingQuestion && !generationStopped && !!answerExtras?.answerId}
        handleStopGeneration={handleStopGeneration}
      />
    </Stack>
  );
};
